import React, { useEffect, useState, useRef } from "react";

const DropdownCheckbox = ({
  label,
  options,
  selectedOptions,
  onSelectAll,
  onOptionChange,
  allSelected,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const renderSelectedChips = () => {
    if (allSelected) {
      return (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-800 truncate">
          All {label}
        </span>
      );
    }

    if (selectedOptions.length === 0) {
      return <span className="text-gray-500 text-[10px] truncate">Select {label}</span>;
    }

    const selectedLabels = selectedOptions.map((value) => {
      const option = options.find((opt) => opt.value === value);
      return option ? option.label : value;
    });

    const buttonWidth = buttonRef.current ? buttonRef.current.offsetWidth - 30 : 100; // Subtract arrow width
    const avgCharWidth = 6; // Approximate pixel width per character
    const chipBaseWidth = 20; // Base width for padding and margin
    let currentWidth = 0;
    let visibleChips = [];
    let remainingCount = 0;

    for (let i = 0; i < selectedLabels.length; i++) {
      const label = selectedLabels[i];
      const chipWidth = label.length * avgCharWidth + chipBaseWidth;
      if (currentWidth + chipWidth <= buttonWidth) {
        visibleChips.push(label);
        currentWidth += chipWidth;
      } else {
        remainingCount = selectedLabels.length - visibleChips.length;
        break;
      }
    }

    return (
      <div className="flex flex-nowrap gap-0.5 items-center overflow-hidden">
        {visibleChips.map((label, index) => (
          <span
            key={index}
            className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-800"
          >
            {label}
          </span>
        ))}
        {remainingCount > 0 && (
          <span className="text-[10px] text-gray-500 whitespace-nowrap">
            +{remainingCount} more
          </span>
        )}
      </div>
    );
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <label className="block text-[10px] font-medium text-gray-700 mb-0.5">{label}</label>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-1.5 py-1 border border-gray-300 rounded-md shadow-sm text-left bg-white flex justify-between items-center focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 min-h-[24px] h-6"
      >
        <div className="flex flex-nowrap gap-0.5 items-center overflow-hidden">
          {renderSelectedChips()}
        </div>
        <svg
          className={`w-4 h-4 ml-1 text-gray-400 transition-transform duration-200 ${isOpen ? "transform rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-10 mt-0.5 w-full bg-white shadow-lg rounded-md border border-gray-200 py-0.5 max-h-48 overflow-auto">
          <div className="px-2 py-1 border-b border-gray-100 bg-gray-50">
            <label className="flex items-center space-x-1 cursor-pointer">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={() => {
                  onSelectAll();
                  setIsOpen(false);
                }}
                className="h-3 w-3 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="text-[10px] text-gray-700">All</span>
            </label>
          </div>

          {options.map((option) => (
            <label
              key={option.value}
              className="flex items-center space-x-1 px-2 py-1 hover:bg-gray-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selectedOptions.includes(option.value)}
                onChange={() => {
                  onOptionChange(option.value);
                  setIsOpen(false);
                }}
                className="h-3 w-3 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="text-[10px] text-gray-700">{option.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

export default DropdownCheckbox;
